import { useLocation } from 'react-router-dom'
import { useNavigate } from '@/hooks/use-navigate'
import { useAuth } from 'react-oidc-context'
import { useCallback, useEffect, useRef } from 'react'
import { STORAGE_KEYS } from '@/lib/constants'
import { LoginModal } from '@/features/authentication'
import useClientQueryString from '@/hooks/use-client-query-string'

/**
 * Renders the login popup whenever the active route is `/internal-login`.
 * Mounted once in the root layout (the React Router equivalent of Next.js'
 * `@modal` parallel slot) so the popup floats over the underlying page.
 */
function InternalLoginModal() {
  const navigate = useNavigate()
  const { isAuthenticated } = useAuth()
  const location = useLocation()
  const pathname = location.pathname
  const clientQs = useClientQueryString()
  const prevAppRef = useRef<string>('')
  const redirectionUriRef = useRef<string>('')
  const returnURLRef = useRef<string>('')

  prevAppRef.current = clientQs.getByKey('applicationReference') ?? ''
  redirectionUriRef.current = clientQs.getByKey('postLoginRedirectionUrl') ?? ''
  returnURLRef.current = clientQs.getByKey('returnUrl') ?? ''

  const isInternalLogin = pathname.includes('internal-login')

  const navigateToPrevPage = useCallback(() => {
    if (returnURLRef.current) {
      navigate(returnURLRef.current, { replace: true })
      return
    }

    if (redirectionUriRef.current) {
      navigate(redirectionUriRef.current, { replace: true })
      return
    }

    const isInvalidAppRef = prevAppRef.current === 'null' || !prevAppRef.current

    navigate(
      `/?${new URLSearchParams({
        ...(!isInvalidAppRef && {
          applicationReference: prevAppRef.current,
        }),
      })}`,
      { replace: true }
    )
  }, [navigate])

  const handleOpenChange = (open: boolean) => {
    if (!open) {
      navigateToPrevPage()
    }
  }

  // Only flag "just logged in" while the login popup itself is open — never on
  // other pages. Mounting this component in the layout means it runs on every
  // route, so guarding on `isInternalLogin` is what prevents an authenticated
  // user from being redirected back to the page they're already on.
  // The actual post-login redirect is handled by usePostLogin/onSuccessLogin.
  useEffect(() => {
    if (isInternalLogin && isAuthenticated) {
      localStorage.setItem(STORAGE_KEYS.CHECK_EXPIRING_POLICIES, 'true')
    }
  }, [isInternalLogin, isAuthenticated])

  if (!isInternalLogin || isAuthenticated) return null

  return <LoginModal onOpenChange={handleOpenChange} />
}

export default InternalLoginModal
